import { CalendarDays, X } from 'lucide-react';
import { useState } from 'react';

import { cn } from '@/utils/cn';
import { formatCalendarDate } from '@/utils/format';

import { Calendar } from './calendar';
import { Popover, PopoverContent, PopoverTrigger } from './popover';
import type { PopoverPortalTarget } from './popover-portal';

export interface DatePickerProps {
  value: string | null;
  onChange: (date: string | null) => void;
  placeholder?: string;
  min?: string;
  max?: string;
  clearable?: boolean;
  clearLabel?: string;
  disabled?: boolean;
  invalid?: boolean;
  id?: string;
  popoverPortal?: PopoverPortalTarget;
  calendarLabels?: {
    month?: string;
    year?: string;
    previousMonth?: string;
    nextMonth?: string;
  };
  className?: string;
  'aria-label'?: string;
  'aria-labelledby'?: string;
  'aria-describedby'?: string;
}

export const DatePicker = ({
  value,
  onChange,
  placeholder,
  min,
  max,
  clearable = false,
  clearLabel,
  disabled = false,
  invalid = false,
  id,
  popoverPortal = 'body',
  calendarLabels,
  className,
  ...aria
}: DatePickerProps) => {
  const [open, setOpen] = useState(false);

  return (
    <Popover open={open} onOpenChange={disabled ? undefined : setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          id={id}
          disabled={disabled}
          aria-invalid={invalid || undefined}
          className={cn(
            'flex h-10 w-full items-center justify-between gap-2 rounded-md border bg-surface px-3 text-left text-body transition-colors',
            'disabled:cursor-not-allowed disabled:bg-surface-sunken disabled:text-fg-disabled',
            invalid ? 'border-danger' : 'border-border hover:border-border-strong',
            className,
          )}
          {...aria}
        >
          <span className="flex min-w-0 items-center gap-2">
            <CalendarDays aria-hidden className="h-4 w-4 shrink-0 text-fg-tertiary" />
            <span className={cn('truncate', value ? 'text-fg' : 'text-fg-tertiary')}>
              {value ? formatCalendarDate(value) : placeholder}
            </span>
          </span>

          {clearable && value && !disabled ? (
            <span
              role="button"
              tabIndex={-1}
              aria-label={clearLabel}
              onPointerDown={(event) => {
                event.preventDefault();
                event.stopPropagation();
                onChange(null);
              }}
              className="rounded p-0.5 text-fg-tertiary hover:bg-surface-sunken hover:text-fg"
            >
              <X className="h-3.5 w-3.5" />
            </span>
          ) : null}
        </button>
      </PopoverTrigger>

      <PopoverContent
        popoverPortal={popoverPortal}
        matchTriggerWidth={false}
        role="presentation"
        className="w-auto p-0"
      >
        <Calendar
          value={value}
          onChange={(date) => {
            onChange(date);
            setOpen(false);
          }}
          min={min}
          max={max}
          popoverPortal={popoverPortal}
          monthLabel={calendarLabels?.month}
          yearLabel={calendarLabels?.year}
          previousMonthLabel={calendarLabels?.previousMonth}
          nextMonthLabel={calendarLabels?.nextMonth}
        />
      </PopoverContent>
    </Popover>
  );
};
